<template>
    <checkout-payment-recovery-error
        v-if="checkTimedOut"
        processing-timed-out
    />

    <section
        v-else
        class="mx-auto flex min-h-[60vh] max-w-2xl items-center px-6 py-16"
    >
        <div
            class="w-full border-l-4 border-warning-500 bg-warning-50 p-8 shadow-sm"
        >
            <div class="mb-4 flex items-center gap-3 text-warning-800">
                <i class="pi pi-spin pi-spinner text-3xl" />
                <h1 class="text-3xl font-bold">
                    {{ t('checkout.paymentProcessingTitle') }}
                </h1>
            </div>
            <p class="text-lg text-gray-700">
                {{ t('checkout.paymentProcessingMessage') }}
            </p>
            <p
                class="mt-6 border-y border-warning-200 py-4 text-center text-4xl font-bold tabular-nums text-warning-900"
                role="timer"
            >
                {{
                    t(
                        'checkout.paymentProcessingCountdown',
                        checkSecondsRemaining
                    )
                }}
            </p>
            <p class="mt-4 text-sm font-medium text-warning-800">
                {{ t('checkout.checkingPaymentStatus') }}
            </p>
        </div>
    </section>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { FragmentType } from '~/resources/graphql'
import { graphql } from '~/resources/graphql'
import CheckoutPaymentRecoveryError from '~/components/Checkout/CheckoutPaymentRecoveryError.vue'
import { CheckoutSuccessFragment } from '~/components/Checkout/CheckoutSuccess.vue'
import { KioskCheckoutResultFragment } from '~/composables/use-kiosk-success'
import { useLogError } from '~/composables/use-log-error'
import { useRpQuery } from '~/composables/graphql'
import { useSelectedLanguage } from '~/composables/use-selected-language'

const props = withDefaults(
    defineProps<{
        checkoutId: string
        kioskCompletion?: boolean
    }>(),
    { kioskCompletion: false }
)

const emit = defineEmits<{
    (
        event: 'checkoutSuccess',
        result: FragmentType<typeof CheckoutSuccessFragment>
    ): void
    (
        event: 'kioskCheckoutSuccess',
        result: FragmentType<typeof KioskCheckoutResultFragment>
    ): void
    (event: 'checkoutStatusChanged'): void
}>()

// PROCESSING can represent a live request, so polling observes state without trying to recover it.
const query = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const logError = useLogError()
const { t } = useI18n({ useScope: 'global' })

// PROCESSING covers the API's work across every payment provider, so allow slow requests two
// minutes to settle before explaining that its result remains uncertain.
const PAYMENT_CHECK_MILLISECONDS = 120_000
const PAYMENT_POLL_MILLISECONDS = 2_000
const checkTimedOut = ref(false)
const checkSecondsRemaining = ref(PAYMENT_CHECK_MILLISECONDS / 1_000)
const isCheckingPayment = ref(false)
let checkDeadline = 0
let checkGeneration = 0
let checkTimer: ReturnType<typeof setTimeout> | undefined
let countdownTimer: ReturnType<typeof setInterval> | undefined

function stopPaymentCheck(): void {
    checkGeneration += 1
    if (checkTimer !== undefined) {
        clearTimeout(checkTimer)
        checkTimer = undefined
    }
    if (countdownTimer !== undefined) {
        clearInterval(countdownTimer)
        countdownTimer = undefined
    }
}

function updatePaymentCountdown(generation: number): void {
    if (generation !== checkGeneration) {
        return
    }

    const remainingMilliseconds = Math.max(0, checkDeadline - Date.now())
    checkSecondsRemaining.value = Math.ceil(remainingMilliseconds / 1_000)
}

function schedulePaymentCheck(generation: number): void {
    if (generation !== checkGeneration) {
        return
    }

    const remainingMilliseconds = checkDeadline - Date.now()
    checkTimer = setTimeout(
        () => void checkPayment(generation),
        Math.max(0, Math.min(PAYMENT_POLL_MILLISECONDS, remainingMilliseconds))
    )
}

async function checkPayment(generation: number): Promise<void> {
    if (generation !== checkGeneration) {
        return
    }
    if (isCheckingPayment.value) {
        schedulePaymentCheck(generation)
        return
    }

    isCheckingPayment.value = true
    try {
        const result = await query(
            graphql(/* GraphQL */ `
                mutation CheckProcessingCheckoutPayment(
                    $input: CheckCheckoutPaymentInput!
                    $language: Language!
                    $isKiosk: Boolean!
                ) {
                    checkCheckoutPayment(input: $input) {
                        __typename
                        ... on Checkout {
                            status
                        }
                        ... on CommitCheckoutResult {
                            ...CheckoutSuccessFragment @skip(if: $isKiosk)
                            ...KioskCheckoutResultFragment
                                @include(if: $isKiosk)
                        }
                    }
                }
            `),
            {
                input: { checkoutId: props.checkoutId },
                language: selectedLanguage.value,
                isKiosk: props.kioskCompletion
            }
        )

        if (generation !== checkGeneration) {
            return
        }

        if (result.checkCheckoutPayment.__typename === 'CommitCheckoutResult') {
            stopPaymentCheck()
            if (props.kioskCompletion) {
                emit('kioskCheckoutSuccess', result.checkCheckoutPayment)
            } else {
                emit('checkoutSuccess', result.checkCheckoutPayment)
            }
            return
        }

        if (result.checkCheckoutPayment.status !== 'PROCESSING') {
            stopPaymentCheck()
            emit('checkoutStatusChanged')
            return
        }
    } catch (error) {
        // A failed observation cannot prove that the original commit stopped or failed.
        logError(error)
    } finally {
        isCheckingPayment.value = false
    }

    if (Date.now() >= checkDeadline) {
        stopPaymentCheck()
        // A timeout changes only the explanation; it never releases the uncertain payment attempt.
        checkTimedOut.value = true
        return
    }

    schedulePaymentCheck(generation)
}

function startPaymentCheck(): void {
    stopPaymentCheck()
    checkTimedOut.value = false
    checkDeadline = Date.now() + PAYMENT_CHECK_MILLISECONDS
    const generation = checkGeneration
    updatePaymentCountdown(generation)
    // Update more frequently than once per second so the rounded display reaches zero on time.
    countdownTimer = setInterval(() => updatePaymentCountdown(generation), 250)
    void checkPayment(generation)
}

onMounted(startPaymentCheck)
onBeforeUnmount(stopPaymentCheck)
</script>
